import { useState } from 'react';
import type { Race } from '../types';
import { useAuth } from '../auth/AuthContext';
import { useUserRaces } from '../context/UserRacesContext';
import { FinishModal } from './FinishModal';
import './RegistrationAction.css';

interface RegistrationActionProps {
  race: Race;
}

// 결승선 깃발 — '완주' 액션을 즉시 읽히게 하는 러너 친화 메타포.
function FlagIcon() {
  return (
    <svg
      className="race-action-icon"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 21V4" />
      <path d="M6 4h12l-2.5 4 2.5 4H6" />
    </svg>
  );
}

// 체크 — '참가 등록'을 완주 깃발과 한눈에 구분시키는 표식.
function CheckIcon() {
  return (
    <svg
      className="race-action-icon"
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

export function RegistrationAction({ race }: RegistrationActionProps) {
  const { user } = useAuth();
  const { registeredIds, finishedIds, addRegistration } = useUserRaces();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finishOpen, setFinishOpen] = useState(false);

  // 등록/완주는 계정에 매이는 1인칭 액션 — 비로그인 뷰어에겐 노출하지 않는다.
  // 게스트는 헤더 또는 북마크를 통해 로그인한다.
  // (CONTEXT.md "Race phase" / 로그인 게이팅 결정 참고)
  if (!user) return null;

  const registered = registeredIds.has(race.id);
  const finished = finishedIds.has(race.id);

  const handleRegister = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await addRegistration(race.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : '등록 처리에 실패했습니다');
    } finally {
      setSubmitting(false);
    }
  };

  // 대회가 끝났으면 완주 기록. fetch 경계에서 날짜 보정된 status 를 신뢰하므로
  // 'completed' 는 곧 "대회일이 지났다"를 뜻한다. (대회일 당일은 미완료로 본다 —
  // 시각 정보가 없어 종료 여부를 알 수 없으므로 다음 날부터 완주 가능.)
  const isFinishable = race.status === 'completed';

  // Already finished → show chip, no button.
  if (finished) {
    return (
      <div className="race-action-group">
        <span className="race-action-chip is-finished" data-testid="finished-chip">
          ✓ 완주 기록 완료
        </span>
      </div>
    );
  }

  if (isFinishable) {
    return (
      <div className="race-action-group">
        <button
          type="button"
          className="race-action-btn is-finish"
          onClick={() => setFinishOpen(true)}
          data-testid="finish-action"
        >
          <FlagIcon />
          완주 기록 남기기
        </button>
        <FinishModal
          open={finishOpen}
          raceId={race.id}
          raceName={race.nameLocal || race.name}
          defaultDistance={race.distances[race.distances.length - 1] ?? null}
          onClose={() => setFinishOpen(false)}
        />
      </div>
    );
  }

  // Open registration / upcoming — show "등록 완료".
  return (
    <div className="race-action-group">
      {registered ? (
        <span className="race-action-chip is-registered" data-testid="registered-chip">
          ✓ 등록 완료
        </span>
      ) : (
        <button
          type="button"
          className="race-action-btn is-register"
          onClick={handleRegister}
          disabled={submitting}
          data-testid="register-action"
        >
          {submitting ? (
            '등록 중…'
          ) : (
            <>
              <CheckIcon />
              참가 등록
            </>
          )}
        </button>
      )}
      {error && (
        <div className="race-action-status" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
